import {
  Breadcrumb,
  Button,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getRequest, postRequest } from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import BulkUploadCsv from "../../../image/AddCsvFile.png";
import { ReactComponent as Archive } from "../../../image/Archive.svg";
import { ReactComponent as Archiveicon } from "../../../image/Archiveicon.svg";
import { ReactComponent as Editicon } from "../../../image/Editicon.svg";
import PlusSquareOutlined from "../../../image/PlusSquareOutlined.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import "../../../styles/Main.scss";
import { authUserData } from "../../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import AddZoneModel from "./AddZoneModel";
import { ReactComponent as Filtericon } from "../../../image/FilterIcon.svg";
import BulkZoneUpload from "./BulkZoneUpload";

const ZoneList = () => {
  const { t } = useTranslation();
  const { Search } = Input;


  // State variables
  const [selectedRow, setSelectedRow] = useState([]); // Selected rows in the table
  const [reload, setReload] = useState(false); // Controls data reload
  const [loading, setLoading] = useState(false); // Loading state for fetching data
  const [zoneList, setZoneList] = useState([]); // List of zones
  const [totalCount, settotalCount] = useState(0); // Total count of zones
  const [zoneId, setZoneId] = useState(null); // ID of the zone being edited
  const [newZoneAdded, setNewZoneAdded] = useState(false); // Tracks if a new zone is added
  const [modalKey, setModalKey] = useState(false); // Key to force modal re-render
  const [searchText, setSearchText] = useState("");

  // State for pagination, sorting, and search 
  const [state, setState] = useState({
    page: 1,
    ordering: "-id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  // Fetch zone list from the API
  useEffect(() => {
    const getZoneList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("zonename/", state);
        if (apiResponse.data.results.success) {
          setZoneList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_zone_list"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getZoneList();
  }, [state, reload, t]);

  // Handle table changes (pagination, sorting, etc.)
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });

    setSelectedRow(selectedRow); // Maintain selected rows
  };

  // Map sorter column and order to API sorting format
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "-id";

    if (sortField === "zone_name" && sortOrder === "ascend") {
      sortingEnumKey = "zone_name";
    }
    if (sortField === "zone_name" && sortOrder === "descend") {
      sortingEnumKey = "-zone_name";
    }
    if (sortField === "id" && sortOrder === "ascend") {
      sortingEnumKey = "id";
    }
    if (sortField === "id" && sortOrder === "descend") {
      sortingEnumKey = "-id";
    }

    return sortingEnumKey;
  };

  // Debounce search input changes
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  const onSearch = (value) => {
    if (value) {
        setState({
            ...state,
            search: value || "",
        });
    } else {
        const { search, ...newState } = state; // Remove 'search' key from state
        setState(newState);
    }
};

  // Row selection handling
  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter(
          (key) => !changeRows.find((row) => row.id === key)
        );
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  // Debounce function for search
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);

  // Table columns configuration
  const columns = [
    {
      title: "Zone ID",
      dataIndex: "id",
      key: "id",
      sorter: true,
    },
    {
      title: "Zone",
      dataIndex: "zone_name",
      key: "zone_name",
      sorter: true,
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: "100px",
      render: (text, record) => (
        <Space>
          <Tooltip
            color={colors}
            placement="left"
            title={
              <span style={{ color: "#fff" }}>
                Edit
              </span>
            }
          >
            {authUserData()?.permission.includes("zone_name|change_zonename") && (
              <Editicon onClick={() => handleEdit(record.id)} style={{ cursor: "pointer" }} />
            )}
          </Tooltip>
          <Tooltip
            color={colors}
            placement="right"
            title={
              <span style={{ color: "#fff" }}>
                Archive
              </span>
            }
          >
            <Popconfirm
              className="action"
              title={t("pop_title")}
              okText={t("pop_ok")}
              cancelText={t("pop_cancel")}
              onConfirm={() => handleArchive(record.id)}
            >
              {authUserData()?.permission.includes(
                "zone_name|delete_zonename"
              ) && <Archive style={{ cursor: "pointer" }} />}
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handle edit zone
  const handleEdit = (id) => {
    setModalKey((prevKey) => !prevKey); // Toggle modal key to force re-render
    setZoneId(id);
    showModal();
  };

  // Handle add zone
  const handleAdd = () => {
    setZoneId(null);
    showModal();
    setReload((reload) => !reload);// Trigger data reload
  };

  // Handle zone archiving
  const handleArchive = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("zonename-delete/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        let totalArchive = totalCount - ids?.length;

        if (totalArchive < 11) {
          setState((prevState) => ({
            ...prevState,
            page: 1,
            search: "",
          }));
          setReload(!reload);
        } else {
          setReload(!reload);
        }
        SuccessNotificationMsg(t("zone_archive_successfully"));

        setSelectedRow([]);
      } else {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: "",
        }));
        ErrorNotificationMsg(t("error_in_archive"));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        page: 1,
        search: "",
      }));
    }
  };

 

  // Modal handlers
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const [isModalOpenBulk, setIsModalOpenBulk] = useState(false);
  const showBulkUpload = () => {
    setIsModalOpenBulk(true);
  };
  const handleOkBulk = () => {
    setIsModalOpenBulk(false);
  };
  const handleCancelBulk = () => {
    setIsModalOpenBulk(false);
  };
  const colors = ["#094E8F"]; // Color for tooltips
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">


            <h3>{t("pl_zone")}</h3>


            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("zone")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>
              </Space>
            </div>
            
            {authUserData()?.permission.includes("zone_name|add_zonename") && (
              <Tooltip
                color={colors}
                placement="right"
                title={
                  <span style={{ color: "#fff" }}>
                    Create New Zone For Organizing & Managing Zone or Pin Code.
                  </span>
                }
              >
                <Button
                  className="BlueShadowBtn"
                  type="primary"
                  onClick={handleAdd}
                  icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
                >
                  {t("pl_add_zone")}
                </Button>
              </Tooltip>
            )}


            <Modal
              className="ModelPopupSec"
              title={zoneId ? t("pl_edit_zone") : t("pl_add_zone")}
              open={isModalOpen}
              onOk={handleOk}
              onCancel={handleCancel}
              closable={false}
              wrapClassName="modal-wrapper"
              keyboard={false}
            >
              <AddZoneModel
                handleCancel={handleCancel}
                setReload={setReload}
                zoneId={zoneId}
                modalKey={modalKey}
                setStateM={setState}

              />
            </Modal>

            <Modal
              className="ModelPopupSec"
              title="Upload File"
              open={isModalOpenBulk}
              onOk={handleOkBulk}
              onCancel={handleCancelBulk}
              closable={false}
              wrapClassName="modal-wrapper"
            >
              <BulkZoneUpload
                setNewZoneAdded={setNewZoneAdded}
                newZoneAdded={newZoneAdded}
                zoneId={zoneId}
                handleCancelBulk={handleCancelBulk}
              />
            </Modal>

            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                {authUserData()?.permission.includes(
                  "zone_name|delete_zonename"
                ) && (
                    <ButtonField
                      className="BlueShadowBtn"
                      type="primary"
                      htmlType="button"
                      label="Archive"
                    />
                  )}
              </Popconfirm>
            )}

            <Tooltip placement="topLeft" title="View Archived Records">
              <Link className="Archiveicon" to="/archive-zone-list">
                <Archiveicon />
              </Link>
            </Tooltip>
            <Tooltip placement="topLeft" title="Bulk Upload">
              <Link className="BulkFileUplodIcon" onClick={showBulkUpload}>
                <img src={BulkUploadCsv} alt="" />
              </Link>
            </Tooltip>
          </Space>
        </div>
      </div>
      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <Row className="tblheader">
          </Row>
          <div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={zoneList}
            loading={loading}
            pagination={{
              current: state.page,
              pageSize: state.pagesize,
              total: totalCount,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: true,
              locale: {
                items_per_page: 'Items / Page', // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
          </div>
        </div>
      </section>
    </>
  );
};
export default ZoneList;
